import * as Sentry from '@sentry/react'
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/react'

const SENSITIVE_FIELD_PATTERN =
  /^(email|password|token|authorization|cookie|upn|pupil_id|student_id|access_token|refresh_token|api_key|secret|forename|surname|first_name|last_name|admission_number|wonde_student_id)$/i

const ALLOWED_URL_PATTERNS: Array<string | RegExp> = [
  /https?:\/\/([\w-]+\.)?academy\.qwixl\.com/i,
  /https?:\/\/localhost(:\d+)?/i,
]

let sentryInitialized = false

function scrubQueryString(url: string): string {
  try {
    const parsed = new URL(url)
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return url.split('?')[0]?.split('#')[0] ?? url
  }
}

function redactSensitiveValue(key: string, value: unknown): unknown {
  if (SENSITIVE_FIELD_PATTERN.test(key)) {
    return '[REDACTED]'
  }
  if (value && typeof value === 'object') {
    return redactSensitiveRecord(value as Record<string, unknown>)
  }
  return value
}

function redactSensitiveRecord(record: Record<string, unknown>): Record<string, unknown> {
  const redacted: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(record)) {
    redacted[key] = redactSensitiveValue(key, value)
  }
  return redacted
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const scrubbed: Breadcrumb = { ...breadcrumb }
  if (typeof scrubbed.message === 'string' && scrubbed.message.includes('?')) {
    scrubbed.message = scrubQueryString(scrubbed.message)
  }
  if (scrubbed.data && typeof scrubbed.data === 'object') {
    const data = { ...scrubbed.data }
    if (typeof data.url === 'string') {
      data.url = scrubQueryString(data.url)
    }
    if (typeof data.from === 'string') {
      data.from = scrubQueryString(data.from)
    }
    if (typeof data.to === 'string') {
      data.to = scrubQueryString(data.to)
    }
    scrubbed.data = redactSensitiveRecord(data as Record<string, unknown>)
  }
  return scrubbed
}

function scrubRequestHeaders(headers: Record<string, string>): Record<string, string> {
  const scrubbed = { ...headers }
  for (const key of Object.keys(scrubbed)) {
    const lower = key.toLowerCase()
    if (lower === 'authorization' || lower === 'cookie') {
      scrubbed[key] = '[REDACTED]'
    }
  }
  return scrubbed
}

function beforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  if (event.request?.url) {
    event.request.url = scrubQueryString(event.request.url)
  }
  if (event.request?.headers) {
    event.request.headers = scrubRequestHeaders(event.request.headers)
  }
  if (event.extra) {
    event.extra = redactSensitiveRecord(event.extra as Record<string, unknown>)
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  }
  return event
}

function resolveSentryEnvironment(): string | null {
  const configured = import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim()
  if (configured) {
    return configured
  }
  // Never fall back to Vite MODE=production on stg hosts — that mis-tags alerts.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host === 'stg.academy.qwixl.com' || host.startsWith('stg.')) {
      return 'staging'
    }
    if (host === 'academy.qwixl.com') {
      return 'production'
    }
  }
  return null
}

export function isSentryEnabled(): boolean {
  return sentryInitialized
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
  if (!dsn) {
    console.error(
      '[Sentry] VITE_SENTRY_DSN is unset — frontend error reporting is disabled; ops paging will not work.',
    )
    return
  }

  const environment = resolveSentryEnvironment()
  if (!environment) {
    console.error(
      '[Sentry] VITE_SENTRY_ENVIRONMENT is unset and host could not be mapped — refusing init so staging is not tagged production.',
    )
    return
  }

  Sentry.init({
    dsn,
    environment,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    allowUrls: ALLOWED_URL_PATTERNS,
    beforeSend,
    initialScope: (scope) => {
      scope.setTag('product', 'academy')
    },
  })
  sentryInitialized = true
}

export { Sentry }
